<script setup>
// SiteUp — Modo Foco ativo: cluster arrastável (posição em % da viewport)
// + seta que abre o menu no lado escolhido (ou automático).
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  provide,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  useFocusMode,
  resolveFocusMenuSide,
} from 'dashboard/composables/useFocusMode';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import FocusModeSettings from './FocusModeSettings.vue';
import FocusModeGuide from './FocusModeGuide.vue';

const LIST_MODES = [
  { id: 'list', icon: 'i-ri-list-check' },
  { id: 'avatars', icon: 'i-ri-user-line' },
  { id: 'hidden', icon: 'i-ri-aspect-ratio-line' },
];

const COMPOSER_SELECTOR = '[data-conversation-composer], .reply-box';
const COMPOSER_GAP_PX = 12;
const DRAG_THRESHOLD_PX = 6;
const EDGE_PAD_PX = 8;

const { t } = useI18n();

const modeLabel = id => {
  if (id === 'list') return t('CHAT_LIST.WORKBENCH.MODE_LIST');
  if (id === 'avatars') return t('CHAT_LIST.WORKBENCH.MODE_AVATARS');
  return t('CHAT_LIST.WORKBENCH.MODE_HIDDEN');
};
const { accountScopedRoute } = useAccount();
const hubRoute = computed(() => accountScopedRoute('focus_hub'));
const {
  shouldShowWelcome,
  disableFocusMode,
  dockPosition,
  saveDockPosition,
  menuSide,
} = useFocusMode();
const { listMode, setListMode } = useWorkbenchLayout();

const settingsRef = ref(null);
const guideRef = ref(null);
const clusterRef = ref(null);
const menuRef = ref(null);
const menuToggleRef = ref(null);
const isMenuOpen = ref(false);
const isFullscreen = ref(false);
const isMoving = ref(false);
const livePosition = ref(null);
const effectiveSide = ref('up');

let pointerOffset = { x: 0, y: 0 };
let pointerStart = { x: 0, y: 0 };
let didDrag = false;

const clusterSize = () => ({
  width: clusterRef.value?.offsetWidth || 54,
  height: clusterRef.value?.offsetHeight || 92,
});

const defaultPosition = () => {
  if (typeof window === 'undefined') return { xPct: 0.01, yPct: 0.84 };
  const { height } = clusterSize();
  const vw = window.innerWidth || 1;
  const vh = window.innerHeight || 1;
  return {
    xPct: EDGE_PAD_PX / vw,
    yPct: Math.max(0, (vh - 16 - height) / vh),
  };
};

const liveComposerRect = () => {
  if (typeof document === 'undefined') return null;
  const composer = document.querySelector(COMPOSER_SELECTOR);
  return composer ? composer.getBoundingClientRect() : null;
};

const clampPosition = pos => {
  if (typeof window === 'undefined') return pos;
  const { width, height } = clusterSize();
  const vw = window.innerWidth || 1;
  const vh = window.innerHeight || 1;
  const maxX = Math.max(0, vw - width);
  const maxY = Math.max(0, vh - height);
  let x = Math.min(maxX, Math.max(0, pos.xPct * vw));
  let y = Math.min(maxY, Math.max(0, pos.yPct * vh));
  const composer = liveComposerRect();
  if (composer) {
    const overlapsX = x < composer.right && x + width > composer.left;
    if (overlapsX) {
      const ceiling = Math.max(0, composer.top - height - COMPOSER_GAP_PX);
      if (y > ceiling) y = ceiling;
    }
  }
  return { xPct: x / vw, yPct: y / vh };
};

const resolvedPosition = computed(() =>
  clampPosition(livePosition.value || dockPosition.value || defaultPosition())
);

const clusterStyle = computed(() => {
  const pos = resolvedPosition.value;
  return {
    left: `${pos.xPct * 100}%`,
    top: `${pos.yPct * 100}%`,
    right: 'auto',
    bottom: 'auto',
  };
});

const syncEffectiveSide = () => {
  const rect = clusterRef.value?.getBoundingClientRect();
  if (!rect || typeof window === 'undefined') return;
  effectiveSide.value = resolveFocusMenuSide(menuSide.value, rect, {
    width: window.innerWidth,
    height: window.innerHeight,
  });
};

const arrowIcon = computed(() => {
  if (effectiveSide.value === 'down') return 'i-ri-arrow-down-s-line';
  if (effectiveSide.value === 'left') return 'i-ri-arrow-left-s-line';
  if (effectiveSide.value === 'right') return 'i-ri-arrow-right-s-line';
  return 'i-ri-arrow-up-s-line';
});

const clusterLayoutClass = computed(() => {
  if (effectiveSide.value === 'down') return 'flex-col-reverse';
  if (effectiveSide.value === 'left') return 'flex-row';
  if (effectiveSide.value === 'right') return 'flex-row-reverse';
  return 'flex-col';
});

const menuPositionClass = computed(() => {
  if (effectiveSide.value === 'down') return 'top-full mt-2 left-0';
  if (effectiveSide.value === 'left') return 'right-full mr-2';
  if (effectiveSide.value === 'right') return 'left-full ml-2';
  return 'bottom-full mb-2 left-0';
});

const menuStyle = computed(() => {
  if (typeof window === 'undefined') return {};
  const rect = clusterRef.value?.getBoundingClientRect();
  if (!rect) return {};
  const gap = 8;
  const side = effectiveSide.value;
  if (side === 'up') {
    return { maxHeight: `${Math.max(160, rect.top - gap)}px` };
  }
  if (side === 'down') {
    return {
      maxHeight: `${Math.max(160, window.innerHeight - rect.bottom - gap)}px`,
    };
  }
  const spaceBelow = window.innerHeight - rect.top;
  const spaceAbove = rect.bottom;
  return {
    maxHeight: `${Math.max(160, window.innerHeight - 16)}px`,
    ...(spaceBelow >= spaceAbove
      ? { top: '0px', bottom: 'auto' }
      : { bottom: '0px', top: 'auto' }),
  };
});

const syncFullscreen = () => {
  isFullscreen.value = Boolean(document.fullscreenElement);
};

const toggleFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen?.();
  } else {
    document.documentElement.requestFullscreen?.();
  }
};

const menuFocusables = () =>
  [...(menuRef.value?.querySelectorAll('a[href],button') || [])].filter(
    el => !el.disabled
  );

const closeMenu = () => {
  isMenuOpen.value = false;
};

const openMenu = () => {
  syncEffectiveSide();
  isMenuOpen.value = true;
  nextTick(() => {
    menuFocusables()[0]?.focus();
  });
};

const toggleMenu = () => {
  if (didDrag) return;
  if (isMenuOpen.value) closeMenu();
  else openMenu();
};

provide('focusModeGuideHost', {
  openMenu,
  closeMenu,
});
provide('startFocusModeTour', () => {
  closeMenu();
  settingsRef.value?.close();
  nextTick(() => guideRef.value?.startTour());
});

const onExitClick = () => {
  if (didDrag) return;
  closeMenu();
  disableFocusMode();
};

const onDocClick = event => {
  if (!isMenuOpen.value) return;
  if (clusterRef.value?.contains(event.target)) return;
  if (event.target.closest?.('[data-testid="focus-mode-tour"]')) return;
  if (event.target.closest?.('[data-testid="focus-mode-welcome"]')) return;
  if (event.target.closest?.('[data-testid="focus-mode-settings"]')) return;
  closeMenu();
};

const onDocKeydown = event => {
  if (event.key !== 'Escape' || !isMenuOpen.value) return;
  closeMenu();
  menuToggleRef.value?.focus();
};

const onMenuKeydown = event => {
  if (event.key !== 'Tab' || !isMenuOpen.value) return;
  const items = menuFocusables();
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const modeStepForKey = key => {
  if (key === 'ArrowDown' || key === 'ArrowRight') return 1;
  if (key === 'ArrowUp' || key === 'ArrowLeft') return -1;
  return 0;
};

const onModeKeydown = event => {
  const step = modeStepForKey(event.key);
  if (!step) return;
  event.preventDefault();
  const ids = LIST_MODES.map(mode => mode.id);
  const current = ids.indexOf(listMode.value);
  const next = ids[(current + step + ids.length) % ids.length];
  setListMode(next);
  nextTick(() => {
    menuRef.value?.querySelector(`[data-mode="${next}"]`)?.focus();
  });
};

const openSettings = () => {
  closeMenu();
  settingsRef.value?.open();
};

const onPointerMove = event => {
  const dx = event.clientX - pointerStart.x;
  const dy = event.clientY - pointerStart.y;
  if (!didDrag && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
  if (!didDrag) {
    didDrag = true;
    isMoving.value = true;
    closeMenu();
  }
  const { width, height } = clusterSize();
  const x = event.clientX - pointerOffset.x;
  const y = event.clientY - pointerOffset.y;
  const vw = window.innerWidth || 1;
  const vh = window.innerHeight || 1;
  const maxX = Math.max(0, vw - width);
  const maxY = Math.max(0, vh - height);
  livePosition.value = clampPosition({
    xPct: Math.min(maxX, Math.max(0, x)) / vw,
    yPct: Math.min(maxY, Math.max(0, y)) / vh,
  });
  nextTick(syncEffectiveSide);
};

const stopMoving = () => {
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', stopMoving);
  window.removeEventListener('pointercancel', stopMoving);
  isMoving.value = false;
  if (didDrag && livePosition.value) {
    saveDockPosition(clampPosition(livePosition.value));
  }
  livePosition.value = null;
  nextTick(syncEffectiveSide);
};

const onPointerDown = event => {
  if (event.button !== 0) return;
  if (menuRef.value?.contains(event.target)) return;
  const rect = clusterRef.value?.getBoundingClientRect();
  if (!rect) return;
  pointerStart = { x: event.clientX, y: event.clientY };
  pointerOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
  didDrag = false;
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', stopMoving);
  window.addEventListener('pointercancel', stopMoving);
};

const onClusterClickCapture = event => {
  if (!didDrag) return;
  event.preventDefault();
  event.stopPropagation();
  didDrag = false;
};

const onWindowResize = () => {
  nextTick(syncEffectiveSide);
};

watch(menuSide, () => nextTick(syncEffectiveSide));

onMounted(() => {
  syncFullscreen();
  nextTick(syncEffectiveSide);
  document.addEventListener('fullscreenchange', syncFullscreen);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onDocKeydown);
  window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', syncFullscreen);
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onDocKeydown);
  window.removeEventListener('resize', onWindowResize);
  stopMoving();
});
</script>

<template>
  <div>
    <div
      ref="clusterRef"
      class="fixed z-[60] flex items-center gap-0.5 rounded-full border border-n-weak/60 bg-n-solid-1/80 p-1 shadow-lg backdrop-blur-xl select-none touch-none"
      :class="[
        clusterLayoutClass,
        isMoving ? 'cursor-grabbing' : 'cursor-grab',
      ]"
      :style="clusterStyle"
      data-testid="focus-mode-cluster"
      @pointerdown="onPointerDown"
      @click.capture="onClusterClickCapture"
    >
      <div
        v-if="isMenuOpen"
        id="focus-mode-menu"
        ref="menuRef"
        class="absolute z-10 w-56 overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-1 p-1.5 shadow-xl"
        :class="menuPositionClass"
        :style="menuStyle"
        data-testid="focus-mode-menu"
        :data-side="effectiveSide"
        :aria-label="t('SIDEBAR.FOCUS_MODE.TITLE')"
        @keydown="onMenuKeydown"
      >
        <RouterLink
          :to="hubRoute"
          class="flex min-h-11 items-center gap-2.5 rounded-xl px-2.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-home"
          @click="closeMenu"
        >
          <span class="i-ri-home-line size-4 shrink-0 text-n-slate-11" />
          {{ t('SIDEBAR.FOCUS_MODE.HOME') }}
        </RouterLink>

        <button
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-fullscreen"
          @click="toggleFullscreen"
        >
          <span
            class="size-4 shrink-0 text-n-slate-11"
            :class="
              isFullscreen
                ? 'i-ri-fullscreen-exit-line'
                : 'i-ri-fullscreen-line'
            "
          />
          {{
            isFullscreen
              ? t('SIDEBAR.FOCUS_MODE.EXIT_FULLSCREEN')
              : t('SIDEBAR.FOCUS_MODE.FULLSCREEN')
          }}
        </button>

        <div class="my-1 border-t border-n-weak" />

        <div
          role="radiogroup"
          class="flex flex-col gap-0.5"
          :aria-label="t('CHAT_LIST.WORKBENCH.MODE_LIST')"
          @keydown="onModeKeydown"
        >
          <button
            v-for="mode in LIST_MODES"
            :key="mode.id"
            type="button"
            role="radio"
            :aria-checked="listMode === mode.id"
            :data-mode="mode.id"
            :data-testid="`focus-mode-mode-${mode.id}`"
            class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm transition-colors hover:bg-n-alpha-2"
            :class="
              listMode === mode.id
                ? 'bg-n-iris-9/15 font-semibold text-n-iris-11'
                : 'text-n-slate-12'
            "
            @click="setListMode(mode.id)"
          >
            <span
              class="size-4 shrink-0"
              :class="[
                mode.icon,
                listMode === mode.id ? 'text-n-iris-11' : 'text-n-slate-11',
              ]"
            />
            <span class="min-w-0 flex-1">{{ modeLabel(mode.id) }}</span>
            <span
              v-if="listMode === mode.id"
              class="i-ri-check-line size-4 shrink-0 text-n-iris-11"
              aria-hidden="true"
            />
          </button>
        </div>

        <div class="my-1 border-t border-n-weak" />

        <button
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-customize"
          @click="openSettings"
        >
          <span class="i-ri-settings-2-line size-4 shrink-0 text-n-slate-11" />
          {{ t('SIDEBAR.FOCUS_MODE.CUSTOMIZE') }}
        </button>

        <div class="my-1 border-t border-n-weak" />

        <button
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-menu-exit"
          @click="onExitClick"
        >
          <span class="i-ri-radio-button-fill size-4 shrink-0 text-n-iris-11" />
          {{ t('SIDEBAR.FOCUS_MODE.DEACTIVATE') }}
        </button>
      </div>

      <button
        ref="menuToggleRef"
        type="button"
        class="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
        data-testid="focus-mode-menu-toggle"
        :aria-expanded="isMenuOpen"
        aria-haspopup="true"
        aria-controls="focus-mode-menu"
        :title="t('SIDEBAR.FOCUS_MODE.REOPEN')"
        :aria-label="t('SIDEBAR.FOCUS_MODE.REOPEN')"
        @click="toggleMenu"
      >
        <span class="size-5" :class="arrowIcon" />
      </button>

      <button
        type="button"
        class="relative flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-n-iris-9 text-white shadow-[0_0_0_4px_rgb(var(--iris-9)/0.28)] transition-transform duration-150 hover:bg-n-iris-10 active:scale-95"
        :class="{
          'ring-2 ring-n-iris-9 ring-offset-2 ring-offset-n-solid-1':
            shouldShowWelcome,
        }"
        data-testid="focus-mode-exit"
        :title="t('SIDEBAR.FOCUS_MODE.DEACTIVATE')"
        :aria-label="t('SIDEBAR.FOCUS_MODE.DEACTIVATE')"
        :aria-pressed="true"
        @click="onExitClick"
      >
        <span class="i-ri-radio-button-fill size-5" />
      </button>
    </div>

    <FocusModeGuide ref="guideRef" />
    <FocusModeSettings ref="settingsRef" />
  </div>
</template>
